import {
  type ElementType,
  type ForwardedRef,
  forwardRef,
  type HTMLProps,
  memo,
  type ReactNode,
  useMemo,
} from 'react';
import {
  Link as ReactRouterLink,
  type LinkProps as ReactRouterLinkProps,
  type To,
} from 'react-router-dom';

export type LinkProps = Omit<ReactRouterLinkProps, 'to' | 'ref'> &
  Omit<HTMLProps<HTMLAnchorElement>, 'ref'> & {
    to?: To;
    isDisabled?: boolean;
    isExternal?: boolean;
    /** HTML element instead of `<a>` when the link is disabled. */
    disabledElement?: keyof JSX.IntrinsicElements;
    children?: ReactNode;
  };

/* A Link that can be disabled. */
export const LinkBase = forwardRef(
  (
    {
      isDisabled = false,
      isExternal = false,
      disabledElement = 'span',
      children = null,
      ...props
    }: LinkProps,
    ref: ForwardedRef<HTMLAnchorElement | HTMLElement>,
  ) => {
    const ExternalLinkComponent: ElementType<any> = isDisabled ? disabledElement : 'a';
    const InternalLinkComponent: ElementType<any> = isDisabled ? disabledElement : ReactRouterLink;

    const LinkComponent = isExternal ? ExternalLinkComponent : InternalLinkComponent;

    const rel = useMemo(
      () => (isExternal && !isDisabled ? 'noopener noreferrer' : undefined),
      [isExternal, isDisabled],
    );

    return (
      <LinkComponent {...props} rel={rel} ref={ref} target={isExternal ? '_blank' : ''}>
        {children}
      </LinkComponent>
    );
  },
);

LinkBase.displayName = 'LinkProxy';

export const Link = memo(LinkBase);
